import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import anylogger from '@app/anylogger'
import { Flex } from './Flex'
import { useCurrentRef } from '@app/hooks'

const log = anylogger('DLWrapper')

export interface ISrcControlCallback {
	name(): string | Blob | undefined
	clear(): void
	load(): void
}

interface DelayedLoadSrcWrapperProps extends React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> {
	refCallback: (ref: any, callback: ISrcControlCallback) => void
}
/**
 * This component can be used to wrap a React Element that normally has a src property. Instead of specifying the src property
 * on the wrapped component (i.e. img, etc) pass the source property to this wrapper.  When the ref for this wrapper is created
 * it will pass
 */
export const DelayedLoadSrcWrapper = React.forwardRef(function DelayedLoadSrcWrapper(props: DelayedLoadSrcWrapperProps, passedRef: any) {
	const { src, refCallback, children, ...rest } = props

	if (!children || children == null) throw new Error(`A single child component must be specified for DelayedLoadSrcWrapper`)
	if (Array.isArray(children)) throw new Error(`DelayedLoadSrcWrapper children cannot be an array`)
	if (children instanceof Element) throw new Error(`The child must be an Element`)

	const [ref, setRef] = useCurrentRef<HTMLDivElement>(passedRef)
	const [url, setUrl] = useState<string | Blob | undefined>('')
	const [visible, setVisible] = useState(false)

	useEffect(() => {
		if (url && src != url) setUrl(src)
	}, [src, url])

	useEffect(() => {
		if (!ref) return

		const load = () => {
			setVisible(true)
		}
		const clear = () => {
			setVisible(false)
		}
		const name = () => {
			return src ?? ''
		}
		refCallback(ref, { load, clear, name })
	}, [refCallback, ref, src, url])

	useEffect(() => {
		if (visible) setUrl(src)
		else setUrl('')
	}, [src, visible])

	const res = React.cloneElement(children as ReactElement, { src: url, ref, ...rest } as any)
	return <Flex ref={setRef}>{res}</Flex>
})
